import { z } from 'zod';
import { AssetTypeSchema, DeviceSlotSchema, ExperimentStateSchema, OutcomeVerdictSchema, TargetSchema } from './domain.js';
import { ControlReferenceSchema } from './export.js';
import { EXPERIMENTS_PATH } from './experiment.js';
/**
 * Lifecycle, Outcome and Creative Learning, as the browser sees them.
 *
 * The shape of this file is the shape of the promise: an Experiment moves through four
 * states without an approval workflow, an Outcome is one enum and nothing else is
 * required, and what a closed Experiment leaves behind is an observation the operator
 * can rewrite, retire or ignore - never a rule that applies itself.
 */
export const LEARNINGS_PATH = '/api/v1/learnings';
export const OUTCOME_EVIDENCE_PATH = '/api/v1/outcome-evidence';
export const OUTCOME_RESOURCE = 'outcome';
export const CREATIVE_LEARNING_RESOURCE = 'creativeLearning';
/** Everything the Learn stage reads, in one request. */
export function lifecyclePath(experimentId) {
    return `${EXPERIMENTS_PATH}/${experimentId}/lifecycle`;
}
export function markLivePath(experimentId) {
    return `${EXPERIMENTS_PATH}/${experimentId}/mark-live`;
}
export function experimentOutcomePath(experimentId) {
    return `${EXPERIMENTS_PATH}/${experimentId}/outcome`;
}
export function outcomeEvidencePath(experimentId) {
    return `${EXPERIMENTS_PATH}/${experimentId}/outcome/evidence`;
}
export function experimentControlPath(experimentId) {
    return `${EXPERIMENTS_PATH}/${experimentId}/control`;
}
export function ignoredLearningsPath(experimentId) {
    return `${EXPERIMENTS_PATH}/${experimentId}/ignored-learnings`;
}
/** Shown when an Experiment was never compared against anything. It blocks nothing. */
export const CONTROL_NOT_CAPTURED = 'Control не зафиксирован';
/** Cross-App suggestions are labelled wherever they appear. Transfer stays visible. */
export const CROSS_APP_LABEL = 'Из другого App';
/**
 * One extracted number, or one the operator typed over it. Both are facts on the same
 * footing: extraction is convenience, never authority, and every one of them is optional.
 */
export const OutcomeFactSchema = z.object({
    key: z.string().min(1),
    label: z.string().min(1),
    value: z.string(),
    unit: z.string().nullable(),
    source: z.enum(['extracted', 'operator']),
});
/** Whether anything could be read out of the evidence. No value here blocks anything. */
export const ExtractionStateSchema = z.enum(['pending', 'extracted', 'nothingFound', 'unavailable']);
export const OutcomeEvidenceResourceSchema = z.object({
    id: z.string().min(1),
    version: z.number().int().nonnegative(),
    kind: z.enum(['text', 'screenshot']),
    /** The pasted text, kept as it was pasted. */
    text: z.string().nullable(),
    /** The dropped Play Console screenshot, kept as it was dropped. */
    artifactId: z.string().nullable(),
    extractionState: ExtractionStateSchema,
    facts: z.array(OutcomeFactSchema),
    createdAt: z.string().min(1),
});
export const OutcomeResourceSchema = z.object({
    id: z.string().min(1),
    version: z.number().int().nonnegative(),
    experimentId: z.string().min(1),
    verdict: OutcomeVerdictSchema,
    /** Required by `Candidate won` and by nothing else. */
    winningCandidateId: z.string().nullable(),
    note: z.string().nullable(),
    resultDate: z.string().min(1),
    evidence: z.array(OutcomeEvidenceResourceSchema),
    createdAt: z.string().min(1),
});
/** A piece of evidence as it is handed over: pasted text, or a screenshot already stored. */
export const OutcomeEvidenceInputSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('text'), text: z.string().min(1) }),
    z.object({ kind: z.literal('screenshot'), artifactId: z.string().min(1) }),
]);
/**
 * Closing. One enum is the whole requirement: a note, a date, evidence and every number
 * are optional, and `Candidate won` is the only value that also needs a winner.
 */
export const RecordOutcomeRequestSchema = z.object({
    verdict: OutcomeVerdictSchema,
    winningCandidateId: z.string().min(1).nullable().optional(),
    note: z.string().nullable().optional(),
    /** Backdating a historical test. Absent in the ordinary flow, which shows no date field. */
    resultDate: z.string().min(1).optional(),
    evidence: z.array(OutcomeEvidenceInputSchema).optional(),
});
/** Editing the facts read out of one piece of evidence. The original is never touched. */
export const UpdateEvidenceRequestSchema = z.object({ facts: z.array(OutcomeFactSchema) });
/**
 * The secondary action for a test that happened before the Studio knew about it. The
 * ordinary flow captures all four moments by itself and shows no date input at all.
 */
export const BackdateRequestSchema = z.object({
    firstExportedAt: z.string().min(1).nullable().optional(),
    markedLiveAt: z.string().min(1).nullable().optional(),
    closedAt: z.string().min(1).nullable().optional(),
    resultDate: z.string().min(1).optional(),
});
/** Correcting the record with the exact asset Play Console was running. */
export const ReplaceControlRequestSchema = z.object({
    artifactIds: z.array(z.string().min(1)).optional(),
    youtubeUrl: z.string().min(1).nullable().optional(),
    note: z.string().nullable().optional(),
});
/** One provenance fact behind a Learning. Facts, never stars and never a confidence score. */
export const ProvenanceFactSchema = z.object({ code: z.string().min(1), statement: z.string().min(1) });
export const LearningSemanticsSchema = z.enum(['candidateWon', 'controlWon', 'noClearWinner']);
export const CreativeLearningResourceSchema = z.object({
    id: z.string().min(1),
    version: z.number().int().nonnegative(),
    appId: z.string().min(1),
    appDisplayName: z.string(),
    experimentId: z.string().nullable(),
    outcomeId: z.string().nullable(),
    target: TargetSchema,
    assetType: AssetTypeSchema,
    hypothesis: z.string().nullable(),
    /** What the operator reads, and may rewrite in place. */
    statement: z.string().min(1),
    /** What was distilled when the Experiment closed. Editing never rewrites this. */
    originalStatement: z.string().min(1),
    semantics: LearningSemanticsSchema,
    /**
     * The comparison, as thumbnails, and both sides are lists because both sides are.
     *
     * A screenshot set is an ordered set on either side, and a single identifier per side
     * could only ever show one of the two Asset Types this Studio is mostly used for.
     * Empty is an ordinary answer - an Experiment closed with no Control captured has no
     * left-hand side and nothing about that blocks anything.
     */
    controlArtifactIds: z.array(z.string().min(1)),
    candidateArtifactIds: z.array(z.string().min(1)),
    resultDate: z.string().min(1),
    facts: z.array(OutcomeFactSchema),
    state: z.enum(['active', 'retired']),
    /** Another Experiment on the same App, Target and Asset Type disagreed. Both are kept. */
    mixedSignal: z.boolean(),
    evidenceCount: z.number().int().nonnegative(),
    provenance: z.array(ProvenanceFactSchema),
    createdAt: z.string().min(1),
    updatedAt: z.string().min(1),
});
/** Beside a Brief, where a Learning from another App is shown, it says so. */
export const LearningSuggestionSchema = z.object({
    learning: CreativeLearningResourceSchema,
    scope: z.enum(['sameApp', 'crossApp']),
});
/**
 * A narrower reading of a Learning, proposed after a later Experiment disagreed in a
 * different Target or Asset Type. It is a proposal: `accept` rewords the original,
 * `split` keeps both as separate Learnings, `leave` changes nothing at all. No decision
 * touches the evidence behind either side.
 */
export const NarrowingDecisionSchema = z.enum(['accept', 'split', 'leave']);
export const LearningNarrowingResourceSchema = z.object({
    id: z.string().min(1),
    version: z.number().int().nonnegative(),
    learningId: z.string().min(1),
    /** The wording as it stands today, so the two can be read side by side. */
    learningStatement: z.string().min(1),
    evidenceLearningId: z.string().min(1),
    proposedStatement: z.string().min(1),
    heldTarget: TargetSchema,
    heldAssetType: AssetTypeSchema,
    contextTarget: TargetSchema,
    contextAssetType: AssetTypeSchema,
    state: z.enum(['proposed', 'accepted', 'split', 'left']),
    createdAt: z.string().min(1),
});
export const ResolveNarrowingRequestSchema = z.object({ decision: NarrowingDecisionSchema });
export const LEARNING_NARROWINGS_PATH = '/api/v1/learning-narrowings';
export const UpdateLearningRequestSchema = z.object({
    statement: z.string().min(1).optional(),
    state: z.enum(['active', 'retired']).optional(),
    facts: z.array(OutcomeFactSchema).optional(),
});
export const IgnoreLearningRequestSchema = z.object({ learningId: z.string().min(1) });
/** A Candidate as the winner picker needs it: the creative, not the provider record. */
export const LifecycleCandidateSchema = z.object({
    candidateId: z.string().min(1),
    label: z.string(),
    artifactId: z.string().nullable(),
    keep: z.boolean(),
});
/**
 * The Learn stage, in one resource. The four timestamps are captured automatically, the
 * Control is either frozen or absent, and the Outcome and the Learning are both nullable
 * because an Experiment may stay Exported or Live for as long as the operator likes.
 */
export const LifecycleResourceSchema = z.object({
    experimentId: z.string().min(1),
    version: z.number().int().nonnegative(),
    appId: z.string().min(1),
    appDisplayName: z.string(),
    assetType: AssetTypeSchema,
    deviceSlot: DeviceSlotSchema.nullable(),
    target: TargetSchema,
    hypothesis: z.string().nullable(),
    state: ExperimentStateSchema,
    createdAt: z.string().min(1),
    firstExportedAt: z.string().nullable(),
    markedLiveAt: z.string().nullable(),
    closedAt: z.string().nullable(),
    /** Frozen once Live, a dated Listing Snapshot preview before it, null when there is none. */
    control: ControlReferenceSchema.nullable(),
    outcome: OutcomeResourceSchema.nullable(),
    learning: CreativeLearningResourceSchema.nullable(),
    candidates: z.array(LifecycleCandidateSchema),
});
//# sourceMappingURL=lifecycle.js.map